// Feature Flag Configuration System
// This file manages all features and their rollout across different clients

export const FEATURES = {
  // Core features (always enabled for stability)
  FINANCE_MODULE: 'finance_module',
  MESSAGES_MODULE: 'messages_module',
  TENANT_PORTAL: 'tenant_portal',
  
  // New features with controlled rollout
  DAILY_MORNING_EMAILS: 'daily_morning_emails',
  AUTOMATED_ALERTS: 'automated_alerts',
  CERTIFICATE_EXPIRY_ALERTS: 'certificate_expiry_alerts',
  BRANCHES: 'branches',
  INSPECTIONS: 'inspections',
  INSPECTION_REPORT_BUILDER: 'inspection_report_builder',
  LANDLORD_LOGIN: 'landlord_login',
  MAINTENANCE_AUTO_REMINDERS: 'maintenance_auto_reminders',
  GLM_GAS_CERT_OCR: 'glm_gas_cert_ocr',
  VENDOR_PORTAL: 'vendor_portal',
};

// Feature rollout configuration per client
export const FEATURE_ROLLOUT_CONFIG = {
  // Core features - always enabled
  [FEATURES.FINANCE_MODULE]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Financial management including rent tracking, payment processing, arrears monitoring, and financial reports',
    stable: true,
  },
  
  [FEATURES.MESSAGES_MODULE]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Internal messaging system for communication between agents, landlords, tenants, and contractors',
    stable: true,
  },
  
  [FEATURES.TENANT_PORTAL]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Self-service portal for tenants',
    stable: true,
  },
  
  // New features with controlled rollout
  [FEATURES.DAILY_MORNING_EMAILS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Send automated agent email digests every morning at 9:00 AM UK time (arrears, inspections, and certificate expiry alerts)',
    stable: true,
  },

  [FEATURES.AUTOMATED_ALERTS]: {
    'demo-plutoo': true,
    'tog-plutoo': false,
    rolloutPercentage: 0,
    description: 'Daily 9:00 AM arrears email digest to agents (requires Finance Module and Daily Morning Emails)',
    stable: false,
  },

  [FEATURES.CERTIFICATE_EXPIRY_ALERTS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Daily 9:00 AM certificate expiry email alerts to agents. Per property, enable landlord reminders in Documents & Certificates for self-managed certs (requires Daily Morning Emails)',
    stable: true,
  },
  
  [FEATURES.BRANCHES]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Multi-branch management with agent assignment and data isolation',
    stable: true,
  },
  
  [FEATURES.INSPECTIONS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Property inspection scheduling, tracking, reporting, and daily 9:00 AM inspection reminder emails (requires Daily Morning Emails)',
    stable: true,
  },

  [FEATURES.INSPECTION_REPORT_BUILDER]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'sgh-plutoo': true,
    rolloutPercentage: 100,
    description: 'Photo-first inspection / inventory report builder with PDF generation and tenant e-sign',
    stable: false,
  },
  
  [FEATURES.LANDLORD_LOGIN]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Allow landlords to login to the webapp (disabling this prevents all landlord access)',
    stable: true,
  },
  
  [FEATURES.MAINTENANCE_AUTO_REMINDERS]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    rolloutPercentage: 100,
    description: 'Automatic reminder emails to tenants for ongoing maintenance requests (sent at day 3, 6, and 9)',
    stable: true,
  },

  [FEATURES.GLM_GAS_CERT_OCR]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'sgh-plutoo': true,
    rolloutPercentage: 100,
    description: 'Gas certificate OCR: analyze uploaded gas certs for address match, Gas ID, and auto-fill expiry date',
    stable: true,
  },

  [FEATURES.VENDOR_PORTAL]: {
    'demo-plutoo': true,
    'tog-plutoo': true,
    'sgh-plutoo': true,
    rolloutPercentage: 100,
    description: 'Public vendor portal: a seller can log in with an email + password set by the agent to view the read-only sales progress of the property they are linked to',
    stable: true,
  },
};

// Helper function to get all features for a client
export const getClientFeatures = (clientName) => {
  const enabledFeatures = {};
  
  Object.keys(FEATURE_ROLLOUT_CONFIG).forEach(feature => {
    const config = FEATURE_ROLLOUT_CONFIG[feature];
    enabledFeatures[feature] = config[clientName] || false;
  });
  
  return enabledFeatures;
};

// Display names for feature cards (no vendor/model names)
const FEATURE_DISPLAY_NAMES = {
  [FEATURES.DAILY_MORNING_EMAILS]: 'Daily morning emails (9 AM)',
  [FEATURES.CERTIFICATE_EXPIRY_ALERTS]: 'Certificate expiry emails',
  [FEATURES.GLM_GAS_CERT_OCR]: 'Gas certificate OCR',
  [FEATURES.INSPECTION_REPORT_BUILDER]: 'Inspection report builder',
};

// Helper function to get user-facing feature name for cards (all caps)
export const getFeatureCardDisplayName = (featureValue, featureKey) => {
  let name;
  if (featureValue != null && FEATURE_DISPLAY_NAMES[featureValue]) {
    name = FEATURE_DISPLAY_NAMES[featureValue];
  } else {
    const raw = featureKey ?? featureValue;
    if (raw == null || typeof raw !== 'string') {
      name = 'Feature';
    } else {
      name = raw.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
  }
  return name.toUpperCase();
};

// Helper function to get feature description
export const getFeatureDescription = (featureName) => {
  return FEATURE_ROLLOUT_CONFIG[featureName]?.description || 'No description available';
};

// Helper function to check if feature is stable
export const isFeatureStable = (featureName) => {
  return FEATURE_ROLLOUT_CONFIG[featureName]?.stable || false;
}; 